// ─── Timezone helpers ─────────────────────────────────────────────────────────
// Bridges the customer's stored timezone (Account.timezone — which may be an
// IANA id like "America/New_York" or a verbose/Windows-style label like
// "(UTC-05:00) Eastern Time (US & Canada)") to IANA ids, and converts between
// UTC instants and wall-clock parts in that zone using only the Intl API
// (no date-fns-tz dependency).
//
// NOTE: This file was reconstructed after it went missing from the working
// tree; the algorithm is the standard Intl-based approach. If the original had
// a larger Windows-label→IANA map, extend WINDOWS_TZ below.
// ──────────────────────────────────────────────────────────────────────────────

const BROWSER_TZ = (() => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  } catch {
    return 'UTC'
  }
})()

// Common verbose / Windows-style labels → IANA. Substring match (case-insensitive).
const WINDOWS_TZ = {
  'eastern time': 'America/New_York',
  'central time': 'America/Chicago',
  'mountain time': 'America/Denver',
  'arizona': 'America/Phoenix',
  'pacific time': 'America/Los_Angeles',
  'alaska': 'America/Anchorage',
  'hawaii': 'Pacific/Honolulu',
  'atlantic time': 'America/Halifax',
  'utc': 'UTC',
  'gmt': 'UTC',
  'london': 'Europe/London',
}

function isValidIana(tz) {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz })
    return true
  } catch {
    return false
  }
}

/** Resolve any stored timezone string to a usable IANA id (falls back to the browser zone). */
export function resolveIanaTz(rawTz) {
  if (!rawTz || typeof rawTz !== 'string') return BROWSER_TZ
  const t = rawTz.trim()
  if (isValidIana(t)) return t
  const lower = t.toLowerCase()
  for (const [needle, iana] of Object.entries(WINDOWS_TZ)) {
    if (lower.includes(needle)) return iana
  }
  return BROWSER_TZ
}

/** Wall-clock parts (year, month 1-12, day, hour 0-23, minute, second) of a UTC instant in `tz`. */
export function wallClockPartsInTz(instant, tz) {
  const date = instant instanceof Date ? instant : new Date(instant)
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: tz || 'UTC',
    hourCycle: 'h23',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  })
  const parts = {}
  for (const p of dtf.formatToParts(date)) {
    if (p.type !== 'literal') parts[p.type] = parseInt(p.value, 10)
  }
  // h23 can emit hour 24 at midnight in some engines — normalise to 0.
  if (parts.hour === 24) parts.hour = 0
  return {
    year: parts.year, month: parts.month, day: parts.day,
    hour: parts.hour, minute: parts.minute, second: parts.second,
  }
}

// Offset (ms) of `tz` at the given UTC instant: (wall-clock-as-UTC) − utc.
function tzOffsetMs(utcMs, tz) {
  const p = wallClockPartsInTz(new Date(utcMs), tz)
  const asIfUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second)
  return asIfUtc - utcMs
}

/** Parse a 'YYYY-MM-DD HH:MM[:SS]' wall-clock string interpreted in `tz` to a UTC ms timestamp. */
export function parseWallClockInTzToMs(str, tz) {
  if (!str) return null
  const m = String(str).match(/(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?/)
  if (!m) return null
  const [, Y, Mo, D, H, Mi, S] = m
  const asUtc = Date.UTC(+Y, +Mo - 1, +D, +H, +Mi, +(S || 0))
  // Correct by the zone offset at that instant; refine once for DST edges.
  let offset = tzOffsetMs(asUtc, tz)
  let ms = asUtc - offset
  const offset2 = tzOffsetMs(ms, tz)
  if (offset2 !== offset) ms = asUtc - offset2
  return ms
}
